import React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cn } from '../../utils/cn';

const TabNavigation = ({
  tabs = [],
  activeTab,
  onTabChange,
  onCreateTab,
  onRemoveTab,
  className = '',
  variant = 'underline', // 'underline', 'pills', 'cards'
  size = 'default', // 'sm', 'default', 'lg'
  fullWidth = false,
  disabled = false,
  allowCreate = true,
  allowRemove = true,
  ...props
}) => {
  const navClasses = cn(
    'flex flex-wrap',
    {
      // Variant styles
      'border-b-2 border-gray-400': variant === 'underline',
      'bg-gray-100 rounded-lg p-1': variant === 'pills',
      'border-b border-gray-200': variant === 'cards',
    },
    {
      // Full width
      'w-full': fullWidth,
    },
    {
      // Spacing
      'mb-4': size === 'sm',
      'mb-6': size === 'default',
      'mb-8': size === 'lg',
    },
    className
  );

  const getTabClasses = (tab, isActive) => {
    const baseClasses = cn(
      'transition-all duration-200 outline-none cursor-pointer font-medium text-center',
      {
        // Size variants
        'px-3 py-2 text-sm': size === 'sm',
        'px-5 py-3 text-base': size === 'default',
        'px-6 py-4 text-lg': size === 'lg',
      },
      {
        // Disabled state
        'opacity-50 cursor-not-allowed': disabled,
        'pointer-events-none': disabled,
      }
    );

    if (variant === 'underline') {
      return cn(
        baseClasses,
        'border-b-2 border-transparent bg-transparent mb-[-2px]',
        isActive
          ? 'text-primary-500 font-semibold border-b-primary-500'
          : 'text-gray-600 hover:text-primary-500'
      );
    }

    if (variant === 'pills') {
      return cn(
        baseClasses,
        'rounded-md',
        isActive
          ? 'bg-white text-primary-600 shadow-sm font-semibold'
          : 'text-gray-600 hover:text-gray-800 hover:bg-white/50'
      );
    }

    if (variant === 'cards') {
      return cn(
        baseClasses,
        'border border-gray-200 bg-white',
        isActive
          ? 'border-primary-500 bg-primary-50 text-primary-600 font-semibold'
          : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50 hover:border-gray-300'
      );
    }

    return baseClasses;
  };

  const handleRemoveTab = (e, tabId) => {
    e.stopPropagation();
    onRemoveTab?.(tabId);
  };

  const isRemovableTab = (tabId) => {
    // Don't allow removing default tabs
    return allowRemove && tabId !== 'home' && tabId !== 'variants';
  };

  const getAddButtonClasses = () => {
    const baseClasses = cn(
      'transition-all duration-200 outline-none cursor-pointer font-medium text-center flex items-center justify-center',
      {
        // Size variants
        'px-3 py-2 text-sm': size === 'sm',
        'px-5 py-3 text-base': size === 'default',
        'px-6 py-4 text-lg': size === 'lg',
      }
    );

    if (variant === 'underline') {
      return cn(
        baseClasses,
        'border-b-2 border-transparent bg-transparent mb-[-2px] text-gray-400 hover:text-gray-600'
      );
    }

    if (variant === 'pills') {
      return cn(
        baseClasses,
        'rounded-md text-gray-400 hover:text-gray-600 hover:bg-white/50'
      );
    }

    if (variant === 'cards') {
      return cn(
        baseClasses,
        'border border-gray-200 bg-white text-gray-400 hover:text-gray-600 hover:bg-gray-50 hover:border-gray-300'
      );
    }

    return baseClasses;
  };

  return (
    <TabsPrimitive.Root
      value={activeTab}
      onValueChange={(id) => {
        if (disabled) return;
        onTabChange(id);
      }}
      {...props}
    >
      <TabsPrimitive.List
        className={navClasses}
        aria-label="Navigation tabs"
      >
        {tabs.map((tab) => {
          const tabId = tab.id || tab.key || tab;
          const tabLabel = tab.label || tab.title || tab;
          const isActive = activeTab === tabId;
          const isTabDisabled = disabled || tab.disabled;
          const canRemove = isRemovableTab(tabId);

          return (
            <div key={tabId} className="relative group">
              <TabsPrimitive.Trigger
                value={tabId}
                disabled={isTabDisabled}
                className={cn(
                  getTabClasses(tab, isActive),
                  canRemove ? 'pr-8' : ''
                )}
              >
                {tab.icon && (
                  <span className="mr-2 inline-flex items-center">
                    {tab.icon}
                  </span>
                )}
                {tabLabel}
                {tab.badge && (
                  <span className={cn(
                    'ml-2 px-2 py-0.5 text-xs font-medium rounded-full',
                    isActive
                      ? 'bg-primary-100 text-primary-600'
                      : 'bg-gray-100 text-gray-600'
                  )}>
                    {tab.badge}
                  </span>
                )}
              </TabsPrimitive.Trigger>

              {canRemove && (
                <button
                  type="button"
                  onClick={(e) => handleRemoveTab(e, tabId)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Remove tab"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          );
        })}

        {allowCreate && onCreateTab && (
          <button
            type="button"
            onClick={onCreateTab}
            className={getAddButtonClasses()}
            title="Add new tab"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          </button>
        )}
      </TabsPrimitive.List>
    </TabsPrimitive.Root>
  );
};

// Tab Content Wrapper Component
export const TabContent = ({
  children,
  activeTab,
  tabId,
  className = '',
  lazy = false,
  keepMounted = false,
  animateTransition = true,
  ...props
}) => {
  const isActive = activeTab === tabId;
  const shouldRender = isActive || keepMounted || (!lazy);

  if (!shouldRender) {
    return null;
  }

  const contentClasses = cn(
    'tab-panel',
    {
      'animate-fade-in': animateTransition && isActive,
      'hidden': !isActive && keepMounted,
    },
    className
  );

  return (
    <div
      role="tabpanel"
      id={`tabpanel-${tabId}`}
      aria-labelledby={`tab-${tabId}`}
      className={contentClasses}
      hidden={!isActive}
      {...props}
    >
      {children}
    </div>
  );
};

// Tab Container - combines navigation and content
export const TabContainer = ({
  children,
  tabs = [],
  defaultTab,
  activeTab: controlledActiveTab,
  onTabChange: controlledOnTabChange,
  className = '',
  navProps = {},
  contentProps = {},
  ...props
}) => {
  const [internalActiveTab, setInternalActiveTab] = React.useState(
    controlledActiveTab || defaultTab || tabs[0]?.id || tabs[0]?.key || tabs[0]
  );

  const isControlled = controlledActiveTab !== undefined;
  const activeTab = isControlled ? controlledActiveTab : internalActiveTab;

  const handleTabChange = (tabId) => {
    if (!isControlled) {
      setInternalActiveTab(tabId);
    }
    controlledOnTabChange?.(tabId);
  };

  return (
    <div className={cn('tab-container', className)} {...props}>
      <TabNavigation
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        {...navProps}
      />
      <div className={cn('tab-content-wrapper', contentProps.className)} {...contentProps}>
        {children}
      </div>
    </div>
  );
};

// Simple Tab Item Component for building tabs array
export const createTab = (id, label, options = {}) => ({
  id,
  label,
  ...options
});

// Predefined tab configurations
export const TAB_VARIANTS = {
  UNDERLINE: 'underline',
  PILLS: 'pills',
  CARDS: 'cards',
};

export const TAB_SIZES = {
  SMALL: 'sm',
  DEFAULT: 'default',
  LARGE: 'lg',
};

export default TabNavigation;